import React from 'react';
import { useQuery } from 'react-query';
import { crmIntegrationService } from '../services/api';

/**
 * Whether a lead reached a dealer's CRM — for admins (their own team) and the
 * super admin. Dealers and executives never see it, and the server refuses
 * them the data too.
 */

export const canSeeCrmSync = (user) => ['super_admin', 'admin'].includes(user?.role);

/** Map of leadId → [{ integration, destination, status, alreadyInCrm, externalId, at }]. */
export function useCrmSyncStatus(leadIds, user) {
  const ids = (leadIds || []).filter(Boolean).map(String);
  const enabled = canSeeCrmSync(user) && ids.length > 0;
  const { data } = useQuery(
    ['crmLeadStatus', ids.join(',')],
    async () => {
      // The server takes up to 200 ids a call; the list can show thousands.
      const chunks = [];
      for (let i = 0; i < ids.length; i += 200) chunks.push(ids.slice(i, i + 200));
      const parts = await Promise.all(chunks.map(c => crmIntegrationService.leadStatus(c)));
      return Object.assign({}, ...parts.map(r => r.data?.data || {}));
    },
    { enabled, staleTime: 30_000, refetchOnWindowFocus: false, retry: false },
  );
  return enabled ? (data || {}) : {};
}

const STYLE = {
  sent:    'bg-green-50 text-green-700 border-green-200',
  pending: 'bg-amber-50 text-amber-700 border-amber-200',
  failed:  'bg-red-50 text-red-700 border-red-200',
};

const label = (e) => {
  if (e.status === 'sent') return e.alreadyInCrm ? 'already in CRM' : 'sent';
  if (e.status === 'pending') return 'sending';
  return 'failed';
};

const title = (e) => [
  `${e.integration}${e.destination ? ` · ${e.destination}` : ''}: ${label(e)}`,
  e.at ? new Date(e.at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '',
  e.externalId ? `CRM ID ${e.externalId}` : '',
  e.status === 'failed' ? 'Ask the super admin to check the sync log' : '',
].filter(Boolean).join('\n');

/** Small chips, one per CRM destination. `detailed` adds time and CRM ID inline. */
export function CrmSyncBadge({ entries, detailed = false }) {
  if (!entries?.length) return null;
  return (
    <div className="flex flex-wrap gap-1 mt-1">
      {entries.map((e, i) => (
        <span
          key={i}
          title={title(e)}
          className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[11px] leading-none ${STYLE[e.status] || STYLE.pending}`}
        >
          {e.integration}{detailed && e.destination ? ` · ${e.destination}` : ''}: {label(e)}
          {detailed && e.at && <span className="opacity-70">· {new Date(e.at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>}
          {detailed && e.externalId && <span className="font-mono opacity-70">· {e.externalId}</span>}
        </span>
      ))}
    </div>
  );
}
